import { Combobox } from '@base-ui/react/combobox';
import { type Ref } from 'react';
import { Checkbox, Chevron } from 'component-library';
import { useDropdownContext } from '../../context/DropdownContext';
import { type DropdownValue, type FlatItem } from '../../types';
import { ItemContent } from '../ItemContent/ItemContent';
import { styles } from './styles';

type DropdownGroupRowProps = {
  item: FlatItem;
  depth: number;
  hasChildren: boolean;
  isExpanded: boolean;
  expandable: boolean;
  onToggleExpanded: (value: DropdownValue) => void;
  dataIndex?: number;
  groupRef?: Ref<HTMLDivElement>;
};

const GroupItemText = ({ item }: { item: FlatItem }) => (
  <div css={styles.ItemText}>
    <ItemContent item={item} />
  </div>
);

export function DropdownGroupRow({
  item,
  depth,
  hasChildren,
  isExpanded,
  expandable,
  onToggleExpanded,
  dataIndex,
  groupRef,
}: DropdownGroupRowProps) {
  const { descendantsByValue, multiple, partialByValue, selectedSet, toggleSelectionForValue } =
    useDropdownContext();
  const descendantValues = descendantsByValue.get(item.value) ?? [];
  const selectedDescendantCount = descendantValues.filter((value) => selectedSet.has(value)).length;
  const isSelected =
    descendantValues.length > 0 && selectedDescendantCount === descendantValues.length;
  const showPartial = partialByValue.get(item.value) === true;
  const showCheckbox = multiple && hasChildren;

  return (
    <Combobox.Group
      ref={groupRef}
      data-index={dataIndex}
      items={descendantValues}
      css={styles.group}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Combobox.GroupLabel css={styles.label}>
        <div css={styles.div({ depth, expandable, showCheckbox })}>
          {showCheckbox && (
            <Checkbox
              checked={isSelected}
              semiChecked={showPartial}
              fullWidth={true}
              onChange={() => {
                toggleSelectionForValue(item.value);
              }}
            />
          )}
          {showCheckbox || expandable ? (
            <button
              type="button"
              aria-expanded={expandable && !showCheckbox ? isExpanded : undefined}
              css={styles.labelTextButton}
              onClick={() => {
                if (showCheckbox) {
                  toggleSelectionForValue(item.value);
                } else if (expandable) {
                  onToggleExpanded(item.value);
                }
              }}
            >
              <GroupItemText item={item} />
            </button>
          ) : (
            <GroupItemText item={item} />
          )}
          {expandable && hasChildren && (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label}`}
              css={styles.labelIcon}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onToggleExpanded(item.value);
              }}
            >
              <Chevron width="24px" height="24px" rotation={isExpanded ? 180 : 0} />
            </button>
          )}
        </div>
      </Combobox.GroupLabel>
    </Combobox.Group>
  );
}
